import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import './MobileRankings.css';
import './customRankings.css';
import UnderConstruction from './UnderConstruction';
import { apiHostname } from '../config';
import { useZone } from '../contexts/ZoneContext';
import { getZoneApi } from '../api'; // 导入API客户端

/**
 * 移动端排名页面组件
 * 显示单打和双打的排名情况
 * 
 * @returns {JSX.Element} 移动端排名页面组件
 */
const MobileRankings = () => {
  console.log('MobileRankings组件渲染');
  
  const [activeTab, setActiveTab] = useState('singles');
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { zone } = useParams(); // 获取URL参数中的区域
  const { currentZone } = useZone(); // 从上下文获取当前区域
  
  // 优先使用URL参数中的区域，如果没有则使用上下文中的区域
  const activeZone = zone || currentZone;
  
  console.log('当前活动区域:', activeZone);

  useEffect(() => {
    console.log('MobileRankings useEffect 执行');
    fetchRankings();
  }, [activeTab, activeZone]); // 当区域或标签改变时重新获取数据

  const fetchRankings = async () => {
    setLoading(true);
    try {
      console.log('开始获取排名数据，区域:', activeZone, '类型:', activeTab);
      
      // 使用API客户端实例替代直接的axios调用
      const zoneApi = getZoneApi(activeZone);
      // 获取排名数据，传递查询参数
      const response = await zoneApi.players.getRankings(activeTab, true);
      
      console.log('API返回的原始数据:', response);
      
      // 检查API返回的数据结构
      let rankingsData = [];
      
      // 处理返回数据的不同可能格式
      if (response && typeof response === 'object') {
        if (response.status === 'success' && response.data) {
          // 新的标准格式
          rankingsData = Array.isArray(response.data.rankings) 
            ? response.data.rankings 
            : Array.isArray(response.data) 
              ? response.data 
              : [];
        } else if (response.success && response.data) {
          // 旧的标准格式
          rankingsData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          // 直接返回数组
          rankingsData = response;
        }
      }
      
      console.log('解析后的排名数据:', rankingsData);
      
      if (!Array.isArray(rankingsData) || rankingsData.length === 0) {
        console.log('没有排名数据可显示');
        setRankings([]);
        setLoading(false);
        return;
      }
      
      // 处理数据，确保所有必要字段都存在
      const processedData = rankingsData
        .map((player) => {
          if (!player) return null;
          
          // 确保ID字段存在且有效
          const playerId = player.playerId || player._id || player.id;
          if (!playerId) {
            console.warn('球员数据缺少ID:', player);
            return null;
          }
          
          // 处理统计数据
          const matches = parseInt(player.matches || player.totalMatches || 0);
          const wins = parseInt(player.wins || 0);
          const losses = parseInt(player.losses || matches - wins || 0);
          const winRate = matches > 0 
            ? ((wins / matches) * 100).toFixed(1) 
            : 0;
          
          return {
            _id: playerId,
            name: player.name || '未命名',
            gender: player.gender || '未知',
            tier: player.tier || 'NEW',
            stats: {
              eloRating: parseFloat(player.rating || player.eloRating || 1500),
              matches: matches,
              wins: wins,
              losses: losses,
              winRate: parseFloat(winRate)
            }
          };
        })
        .filter(player => player !== null);
      
      console.log('处理后的排名数据:', processedData);
      
      // 按积分从高到低排序
      const sortedRankings = processedData.sort((a, b) => {
        const ratingDiff = b.stats.eloRating - a.stats.eloRating;
        if (ratingDiff !== 0) return ratingDiff;
        // 如果积分相同，按胜率排序
        return b.stats.winRate - a.stats.winRate;
      });
      
      setRankings(sortedRankings);
    } catch (err) {
      console.error('获取排名失败:', err);
      setError('获取排名数据失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    // 生成模拟数据用于开发测试
    const mockPlayers = [];
    const names = ['乐琪', '静琦', '志清', '嘉琪', '文超', '文聪', '王飞', '李强', '张伟', '刘洋', 
                  '陈明', '赵红', '周杰', '吴刚', '郑华', '孙亮', '马超', '高峰', '林涛', '黄健'];
    
    for (let i = 0; i < 20; i++) {
      const eloRating = 2500 - i * 25;
      const winRate = 100 - Math.floor(Math.random() * 40);
      const matches = 10 + Math.floor(Math.random() * 40);
      const wins = Math.floor(matches * (winRate / 100));
      const losses = matches - wins;
      
      // 根据排名位置分配等级
      let tier;
      if (i < 2) {
        tier = 'S';
      } else if (i < Math.ceil(20 * 0.1)) {
        tier = 'T0';
      } else if (i < Math.ceil(20 * 0.3)) {
        tier = 'T1';
      } else if (i >= Math.floor(20 * 0.8)) {
        tier = 'T3';
      } else {
        tier = 'T2';
      }
      
      mockPlayers.push({
        _id: new Array(24).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join(''),
        name: names[i],
        gender: Math.random() > 0.5 ? '男' : '女',
        stats: {
          eloRating: eloRating,
          winRate: winRate,
          totalMatches: matches,
          wins: wins,
          losses: losses
        },
        tier
      });
    }
    
    return mockPlayers;
  };

  const getLevelClassName = (tier) => {
    const tierLower = tier ? tier.toLowerCase() : 'new';
    return `tier-tag-${tierLower}`;
  };

  const handlePlayerClick = (playerId) => {
    console.log('点击球员:', playerId, '区域:', activeZone);
    
    // 检查playerId是否有效
    if (!playerId || playerId === 'undefined') {
      console.error('无效的球员ID:', playerId);
      return;
    }
    
    // 检查zone是否有效
    if (!activeZone) {
      console.error('缺少区域参数');
      return;
    }
    
    // 导航到球员详情页面
    navigate(`/badminton/${activeZone}/players/${playerId}`);
  };

  const getLastUpdateTime = () => {
    const now = new Date();
    return `最后更新时间: ${now.getFullYear()}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getDate().toString().padStart(2, '0')} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  };

  // 按照分档对排名进行分组
  const groupRankingsByTier = () => {
    const groups = {
      'S': [],
      'T0': [],
      'T1': [],
      'T2': [],
      'T3': [],
      'NEW': []
    };
    
    rankings.forEach((player, index) => {
      const tier = player.tier;
      if (groups[tier]) {
        groups[tier].push({...player, rank: index + 1});
      } else {
        groups['NEW'].push({...player, rank: index + 1});
      }
    });
    
    return groups;
  };

  if (loading) {
    console.log('显示加载中...');
    return <div className="mobile-loading">加载中...</div>;
  }

  if (error) {
    console.log('显示错误:', error);
    return <div className="mobile-error">{error}</div>;
  }

  console.log('渲染排名列表, 当前子标签:', activeTab);

  return (
    <div className="mobile-rankings">
      <div className="mobile-rankings-header">
        <div className="mobile-rankings-tabs">
          <button 
            className={`tab-button ${activeTab === 'singles' ? 'active' : ''}`}
            onClick={() => setActiveTab('singles')}
            type="button"
          >
            <span>单打榜</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'doubles' ? 'active' : ''}`}
            onClick={() => setActiveTab('doubles')}
            type="button"
          >
            <span>双打榜</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'rules' ? 'active' : ''}`}
            onClick={() => setActiveTab('rules')}
            type="button"
          >
            <span>规则</span>
          </button>
        </div>
      </div>

      <div className="mobile-rankings-update-time">
        {getLastUpdateTime()}
      </div>

      {activeTab !== 'rules' ? (
        <div className="mobile-rankings-list">
          {rankings.length === 0 ? (
            <div className="no-data">暂无排名数据</div>
          ) : (
            rankings.map((player, index) => (
              <div 
                key={player._id} 
                className={`mobile-ranking-item tier-row-${player.tier ? player.tier.toLowerCase() : 'new'}`}
                onClick={() => handlePlayerClick(player._id)}
              >
                <div className="ranking-position">{index + 1}</div>
                <div className="player-info">
                  <div className="player-name">
                    {player.name}
                    <span className={`player-gender ${player.gender === 'FEMALE' ? 'female' : 'male'}`}>
                      {player.gender === 'FEMALE' ? '女' : '男'}
                    </span>
                    <span className={getTierClassName(player.tier)}>
                      {getTierName(player.tier)}
                    </span>
                  </div>
                </div>
                <div className="player-score">{Math.round(player.stats.eloRating)}</div>
                <div className="player-stats">
                  <div className="win-rate">{player.stats.winRate}%</div>
                  <div className="match-count">{player.stats.wins}胜{player.stats.losses}负</div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="ranking-rules">
          <h2>积分规则说明</h2>
          <p>我们采用ELO积分系统来计算球员的排名积分，这是一种广泛应用于竞技体育和游戏的评级系统。</p>
          
          <h3>基本规则</h3>
          <ul>
            <li>每位新球员的初始积分为1500分</li>
            <li>比赛后，胜者获得积分，败者失去积分</li>
            <li>积分变化取决于双方的积分差距</li>
            <li>积分差距越大，低分球员赢得高分球员时获得的积分越多</li>
            <li>积分差距越大，高分球员赢得低分球员时获得的积分越少</li>
          </ul>
          
          <h3>段位划分</h3>
          <ul>
            <li><strong>S级</strong>: 1800分以上</li>
            <li><strong>T1级</strong>: 1650-1799分</li>
            <li><strong>T2级</strong>: 1500-1649分</li>
            <li><strong>T3级</strong>: 1350-1499分</li>
            <li><strong>NEW</strong>: 少于5场比赛的新球员</li>
          </ul>
          
          <p>积分系统会持续调整，以确保公平竞争和准确反映球员的实际水平。</p>
        </div>
      )}
    </div>
  );
};

// 获取等级名称
const getTierName = (tier) => {
  switch (tier) {
    case 'S': return 'S';
    case 'T0': return 'T0';
    case 'T1': return 'T1';
    case 'T2': return 'T2';
    case 'T3': return 'T3';
    default: return 'NEW';
  }
};

// 获取等级样式类名
const getTierClassName = (tier) => {
  switch (tier) {
    case 'S': return 'tier-tag-s';
    case 'T0': return 'tier-tag-t0';
    case 'T1': return 'tier-tag-t1';
    case 'T2': return 'tier-tag-t2';
    case 'T3': return 'tier-tag-t3';
    default: return 'tier-tag-new';
  }
};

export default MobileRankings;